(() => {
  const config = window.themeInteractionAssets || { groups: {} };
  const groupPromises = new Map();
  const scriptPromises = new Map();
  const stylePromises = new Map();

  const absoluteUrl = (url) => new URL(url, document.baseURI).href;

  const loadStyle = (url) => {
    if (!url) return Promise.resolve();
    const href = absoluteUrl(url);
    if (stylePromises.has(href)) return stylePromises.get(href);

    const promise = new Promise((resolve) => {
      const existing = [...document.querySelectorAll('link[rel="stylesheet"]')]
        .find((link) => link.href === href);
      if (existing?.sheet) {
        resolve();
        return;
      }

      const link = existing || document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.interactionStylesheet = '';
      link.addEventListener('load', resolve, { once: true });
      link.addEventListener('error', resolve, { once: true });
      if (!existing) document.head.appendChild(link);
    });

    stylePromises.set(href, promise);
    return promise;
  };

  const loadScript = (url) => {
    if (!url) return Promise.resolve();
    const src = absoluteUrl(url);
    if (scriptPromises.has(src)) return scriptPromises.get(src);

    const existing = [...document.scripts].find((script) => script.src === src);
    if (existing) {
      const resolved = Promise.resolve();
      scriptPromises.set(src, resolved);
      return resolved;
    }

    const promise = new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = src;
      script.async = false;
      script.dataset.interactionScript = '';
      script.addEventListener('load', resolve, { once: true });
      script.addEventListener('error', resolve, { once: true });
      document.head.appendChild(script);
    });

    scriptPromises.set(src, promise);
    return promise;
  };

  const loadGroup = (name) => {
    if (groupPromises.has(name)) return groupPromises.get(name);
    const group = config.groups?.[name];
    if (!group) return Promise.resolve();

    const promise = (async () => {
      for (const dependency of group.dependencies || []) {
        await loadGroup(dependency);
      }

      await Promise.all((group.styles || []).filter(Boolean).map(loadStyle));
      for (const script of (group.scripts || []).filter(Boolean)) {
        await loadScript(script);
      }

      document.dispatchEvent(new CustomEvent('theme-assets:loaded', { detail: { group: name } }));
    })();

    groupPromises.set(name, promise);
    return promise;
  };

  window.themeAssets = { loadGroup, loadScript, loadStyle };

  const closest = (target, selector) => target instanceof Element ? target.closest(selector) : null;
  const rivoLoginSelector = 'a.rivo-external-login-button';
  const localizeRivoLogin = (link) => {
    if (!link || !config.storefrontLoginUrl) return;
    try {
      const preview = window.location.hostname.endsWith('.shopifypreview.com');
      const base = preview
        ? document.querySelector('link[rel="canonical"]')?.href
        : window.location.href;
      if (!base) return;
      const loginUrl = new URL(config.storefrontLoginUrl, base);
      if (loginUrl.pathname !== '/customer_authentication/login') return;
      const returnUrl = preview
        ? new URL(loginUrl.searchParams.get('return_to') || '/', base)
        : new URL(window.location.href);
      if (returnUrl.origin !== loginUrl.origin) return;
      returnUrl.hash = '';
      returnUrl.searchParams.set('show_rivo_account', 'true');
      loginUrl.searchParams.set('return_to', returnUrl.pathname + returnUrl.search);
      const href = preview ? loginUrl.href : loginUrl.pathname + loginUrl.search;
      if (link.getAttribute('href') !== href) link.setAttribute('href', href);
    } catch {}
  };
  const localizeRivoLinks = (root) => {
    if (!(root instanceof Element)) return;
    if (root.matches(rivoLoginSelector)) localizeRivoLogin(root);
    root.querySelectorAll(rivoLoginSelector).forEach(localizeRivoLogin);
  };
  localizeRivoLinks(document.documentElement);
  new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'attributes' && record.target.matches(rivoLoginSelector)) {
        localizeRivoLogin(record.target);
      }
      for (const node of record.addedNodes) localizeRivoLinks(node);
    }
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
  for (const eventName of ['pointerdown', 'focusin', 'contextmenu', 'auxclick']) {
    document.addEventListener(eventName, (event) => localizeRivoLogin(closest(event.target, rivoLoginSelector)), true);
  }
  const cartSelector = '#cart-icon-bubble, cart-drawer, card-variant-size';
  const productSelector = 'product-form, quick-view, .quick-add';
  const searchSelector = 'drawer-button[data-target="drawer-search"], fake-search-input, [data-fake-input], #drawer-search';
  const warmCartAssets = () => loadGroup('cart').then(() => {
    document.querySelector('cart-drawer')?.loadStyles?.();
  });

  const warmAssets = (event) => {
    if (closest(event.target, cartSelector)) warmCartAssets();
    if (closest(event.target, productSelector)) loadGroup('product');
    if (closest(event.target, searchSelector)) {
      loadGroup('search').then(() => {
        const fakeInput = closest(event.target, '[data-fake-input]');
        const fakeSearch = fakeInput?.closest('fake-search-input');
        if (fakeInput && document.activeElement === fakeInput) fakeSearch?.handleFocus?.();
      });
    }
  };

  document.addEventListener('focusin', warmAssets, true);

  document.addEventListener('pointerover', (event) => {
    if (closest(event.target, '#cart-icon-bubble')) warmCartAssets();
  }, { capture: true, passive: true });

  document.addEventListener('click', (event) => {
    localizeRivoLogin(closest(event.target, rivoLoginSelector));

    const cartTrigger = closest(event.target, '#cart-icon-bubble');
    if (cartTrigger && !customElements.get('cart-drawer')) {
      event.preventDefault();
      event.stopImmediatePropagation();
      loadGroup('cart').then(() => {
        const drawer = document.querySelector('cart-drawer');
        if (typeof drawer?.open === 'function') drawer.open(cartTrigger);
        else window.location.assign(cartTrigger.href);
      });
      return;
    }

    const sizeSelector = closest(event.target, 'card-variant-size');
    if (sizeSelector && !customElements.get('cart-drawer') && !event.target.dataset.assetReplay) {
      event.preventDefault();
      event.stopImmediatePropagation();
      loadGroup('cart').then(() => {
        event.target.dataset.assetReplay = 'true';
        event.target.click();
        delete event.target.dataset.assetReplay;
      });
      return;
    }

    if (closest(event.target, 'quick-view')) loadGroup('quickView');
    if (closest(event.target, searchSelector)) loadGroup('search');
  }, true);

  document.addEventListener('submit', (event) => {
    const form = closest(event.target, 'product-form form');
    if (!form || customElements.get('product-form')) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    loadGroup('product').then(() => form.requestSubmit(submitter));
  }, true);
})();
